import { Emitter } from "@pixi/particle-emitter";
import { IGameAnimation } from "../../../../../animations/IGameAnimation";
import { Container, Point, Texture } from "pixi.js";
import { PromiseCompletionSource } from "by/gamefactory/shared/async/PromiseCompletionSource";

export class FlyToTargetAnimation implements IGameAnimation {
    private animationContainer: Container;
    private particles: Texture[];
    private options: FlyToTargetAnimationOptions;

    public constructor(
        animationContainer: Container,
        particles: Texture[],
        options: FlyToTargetAnimationOptions
    ) {
        this.animationContainer = animationContainer;
        this.particles = particles;
        this.options = options;
    }

    public async play(): Promise<void> {
        const from = this.options.from;
        const target = this.options.target;
        const duration = this.options.duration;
        const particlesAmount = this.options.particlesAmount;
        const emitter = new Emitter(this.animationContainer, {
            "lifetime": {
                "min": duration,
                "max": duration
            },
            "emit": false,
            "autoUpdate": true,
            "frequency": 1 / particlesAmount,
            "emitterLifetime": duration,
            "maxParticles": particlesAmount,
            "addAtBack": false,
            "pos": {
                "x": 0,
                "y": 0
            },
            "behaviors": [
                {
                    "type": "alpha",
                    "config": {
                        "alpha": {
                            "list": [
                                {
                                    "time": 0,
                                    "value": 0.0
                                },
                                {
                                    "time": 0.2,
                                    "value": 1.0
                                },
                                {
                                    "time": 0.8,
                                    "value": 1.0
                                },
                                {
                                    "time": 0.85,
                                    "value": 0.0
                                },
                                {
                                    "time": 1,
                                    "value": 0.0
                                }
                            ]
                        }
                    }
                },
                {
                    "type": "movePath",
                    "config": {
                        "path": "sin(x/180)*110-10",
                        "speed": {
                            "list": [
                                {
                                    "time": 0,
                                    "value": 0
                                },
                                {
                                    "time": 1,
                                    "value": target.x
                                }
                            ]
                        },
                        "minMult": 1
                    }
                },
                {
                    "type": "scale",
                    "config": {
                        "scale": {
                            "list": [
                                {
                                    "time": 0,
                                    "value": 0.8
                                },
                                {
                                    "time": 0.75,
                                    "value": 0.75
                                },
                                {
                                    "time": 0.8,
                                    "value": 0.05
                                },
                                {
                                    "time": 1,
                                    "value": 0.05
                                }
                            ]
                        },
                        "minMult": 1
                    }
                },
                {
                    "type": "textureRandom",
                    "config": {
                        "textures": this.particles
                    }
                },
                {
                    "type": "spawnShape",
                    "config": {
                        "type": "torus",
                        "data": {
                            "radius": 30,
                            "x": from.x,
                            "y": from.y,
                            "innerRadius": 9,
                            "rotation": true
                        }
                    }
                }
            ]
        });
        const untilComplete = new PromiseCompletionSource<void>();
        emitter.playOnce(() => {
            untilComplete.complete();
        });
        await untilComplete.result;
        emitter.destroy();
    }

}

export interface FlyToTargetAnimationOptions {
    from: Point;
    target: Point;
    duration: number;
    particlesAmount: number;
}